import React from 'react';
import type { IconKind } from '../Icon/Icon';
import { Icon } from '../Icon/Icon';
import type { MiToneKey } from '../../growth-market/types';

export const MI_TONE: Record<MiToneKey, { accent: string; tint: string }> = {
  ai: { accent: 'var(--ai-3)', tint: 'var(--ai-tint)' },
  info: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: { accent: 'var(--terracotta)', tint: 'var(--terracotta-tint)' },
  lav: { accent: 'var(--ai-4)', tint: 'var(--ai-tint)' },
  neutral: { accent: 'var(--ink-3)', tint: 'var(--surface-mute)' },
};

export const miEyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink-3)]';

export function MiCard({
  children,
  className = '',
  padding = true,
}: {
  children: React.ReactNode;
  className?: string;
  padding?: boolean;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        padding ? 'p-[22px]' : '',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

export function MiSectionHead({
  icon,
  tone = 'ai',
  title,
  sub,
  right,
}: {
  icon?: IconKind;
  tone?: MiToneKey;
  title: string;
  sub?: string;
  right?: React.ReactNode;
}) {
  const t = MI_TONE[tone];
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          {icon ? (
            <span
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
              style={{ background: t.tint, color: t.accent }}
            >
              <Icon kind={icon} size={15} stroke={1.9} />
            </span>
          ) : null}
          <span className="font-[var(--font-display)] text-[16.5px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
            {title}
          </span>
        </div>
        {right}
      </div>
      {sub ? (
        <div
          className="mt-[5px] text-xs leading-[1.5] text-[var(--ink-3)]"
          style={{ marginLeft: icon ? 38 : 0 }}
        >
          {sub}
        </div>
      ) : null}
    </div>
  );
}

export function MiBadge({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: MiToneKey }) {
  const t = MI_TONE[tone];
  return (
    <span
      className="inline-flex items-center gap-[5px] rounded-[var(--r-pill)] px-2.5 py-[3px] text-[11.5px] font-semibold"
      style={{ background: t.tint, color: t.accent }}
    >
      {children}
    </span>
  );
}

export function MiAutopilot() {
  return (
    <span className="inline-flex items-center gap-[5px] rounded-[var(--r-pill)] border border-[color-mix(in_srgb,var(--ai-3)_24%,transparent)] bg-[var(--ai-tint)] px-2.5 py-[3px] text-[10px] font-bold uppercase tracking-[0.05em] text-[var(--ai-3)]">
      <Icon kind="bolt" size={11} stroke={2.2} />
      Feeds Autopilot
    </span>
  );
}

export function MiBar({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: MiToneKey;
}) {
  const color = tone
    ? MI_TONE[tone].accent
    : value > 60
      ? 'var(--ai-3)'
      : value > 35
        ? 'var(--gold)'
        : 'var(--cream-shadow)';

  return (
    <div className="mb-[11px]">
      <div className="mb-1 flex justify-between">
        <span className="text-[12.5px] text-[var(--ink-2)]">{label}</span>
        <span className="w-[34px] text-right font-[var(--font-mono)] text-[11.5px] font-bold text-[var(--ink-1)]">
          {value}%
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded bg-[var(--surface-mute)]">
        <div className="h-full rounded" style={{ width: `${value}%`, background: color }} />
      </div>
    </div>
  );
}

export function MiAiBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="elva-grad-ink relative overflow-hidden rounded-[var(--r-xl)] p-[22px_24px] text-white shadow-[var(--sh-2)]">
      <div className="elva-grad-spectrum-fill pointer-events-none absolute -right-10 -top-[70px] h-[200px] w-[200px] rounded-full opacity-[0.36] blur-[60px]" />
      <div className="relative">
        <div className="mb-4 flex items-center gap-2">
          <Icon kind="sparkle" size={15} stroke={2} className="text-white" />
          <span className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-white/80">
            {title}
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}

export function MiAiList({
  label,
  items,
  divider,
}: {
  label: string;
  items: string[];
  divider?: boolean;
}) {
  return (
    <div
      className="mb-4"
      style={{
        paddingTop: divider ? 14 : 0,
        borderTop: divider ? '1px solid rgba(255,255,255,0.15)' : undefined,
      }}
    >
      <div className="mb-2 text-[12.5px] font-semibold text-white/90">{label}</div>
      <div className="flex flex-col gap-2">
        {items.map((item) => (
          <div key={item} className="flex items-start gap-2">
            <span className="mt-[7px] h-[5px] w-[5px] shrink-0 rounded-full bg-[var(--ai-g2,#C9B6FF)]" />
            <span className="text-[12.5px] leading-[1.55] text-white/85">{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function deltaToneClass(tone: MiToneKey | 'neutral'): string {
  if (tone === 'success') return 'text-[var(--success)]';
  if (tone === 'terra') return 'text-[var(--terracotta)]';
  return 'text-[var(--ink-3)]';
}
